import type { BalanceSlot } from '@sportninja/common/services/teamBalance'
import { matchesRowFilter, type BalanceRowFilter } from './balanceRowFilter'

export type CancelledToggleReason = 'none' | 'filtered'

export type CancelledToggleState = {
  /** Cancelled slots the CURRENT Show filter would admit — what toggling reveals. */
  eligibleCount: number
  /** Cancelled slots on the sheet at all, ignoring Show. Distinguishes the two reasons. */
  rawCancelledCount: number
  disabled: boolean
  /** Why it is disabled — `null` whenever it is not. */
  reason: CancelledToggleReason | null
}

/**
 * SN-8346 — QA filed "Display Cancelled" as dead UI. It is not: `BalanceTab`
 * filters on it and a test pins that. It only LOOKS dead, because flipping it
 * can have nothing to reveal.
 *
 * Three ways that happens:
 *  - the sheet has no cancelled slots at all (QA's case) — `reason: 'none'`;
 *  - Show is Owes money / Open slots, which a cancelled slot can never match
 *    (expected 0 ⇒ `owed = −paid ≤ 0`, and it is occupied) — `reason: 'filtered'`;
 *  - a roster / pinned-scope switch reset it to OFF, which is deliberate.
 *
 * So the toggle is disabled with an explanation rather than left looking broken.
 *
 * NEVER disabled while it is ON: the user would be trapped with rows they could
 * not hide again. Only the OFF state can be a dead end, and only then.
 */
export const cancelledToggleState = ({
  slots,
  rowFilter,
  showCancelled,
}: {
  slots: BalanceSlot[]
  rowFilter: BalanceRowFilter
  showCancelled: boolean
}): CancelledToggleState => {
  let eligibleCount = 0
  let rawCancelledCount = 0

  for (const slot of slots) {
    if (slot.status !== 'cancelled') {
      continue
    }
    rawCancelledCount += 1
    if (matchesRowFilter(slot, rowFilter)) {
      eligibleCount += 1
    }
  }

  const disabled = !showCancelled && eligibleCount === 0

  return {
    eligibleCount,
    rawCancelledCount,
    disabled,
    reason: !disabled ? null : rawCancelledCount === 0 ? 'none' : 'filtered',
  }
}
